import { crc32 } from './crc32';

/**
 * The other half of `zip.ts`. Reads what we wrote, and also what a user gets
 * if they open a backup, change something and zip it back up — hence deflate
 * support here but not there.
 *
 * Entries are found through `Blob.slice`, which doesn't copy: opening a two
 * gigabyte backup reads its central directory and nothing else. Reading an
 * entry then copies that one entry out, for a reason `read` explains — so a
 * photo costs memory while it is read and until whoever asked for it is done,
 * but the archive around it never does.
 */

const SIGNATURE = { local: 0x04034b50, central: 0x02014b50, end: 0x06054b50 } as const;

const STORED = 0;
const DEFLATED = 8;

/** The end record, plus the largest comment that can follow it. */
const TAIL = 22 + 0xffff;

export class ZipFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ZipFormatError';
  }
}

type Entry = {
  name: string;
  method: number;
  crc: number;
  /** Bytes on disk. */
  compressed: number;
  /** Bytes once unpacked. */
  size: number;
  /** Of the local header, which is where the data offset has to be read from. */
  header: number;
};

export type ZipReader = {
  /** Entry names, in the order the directory lists them. */
  names: string[];
  has: (name: string) => boolean;
  /** Undefined for a name the archive doesn't hold. */
  blob: (name: string) => Promise<Blob | undefined>;
  text: (name: string) => Promise<string | undefined>;
};

export async function openZip(file: Blob): Promise<ZipReader> {
  const entries = await readDirectory(file);

  async function blob(name: string): Promise<Blob | undefined> {
    const entry = entries.get(name);
    return entry === undefined ? undefined : await read(file, entry);
  }

  return {
    names: [...entries.keys()],
    has: (name) => entries.has(name),
    blob,
    text: async (name) => await (await blob(name))?.text(),
  };
}

async function readDirectory(file: Blob): Promise<Map<string, Entry>> {
  const tail = new Uint8Array(await file.slice(Math.max(0, file.size - TAIL)).arrayBuffer());
  const view = new DataView(tail.buffer);

  // Backwards from the end: the comment that may follow the record can itself
  // contain the signature, and the last match is the real one.
  let at = tail.length - 22;
  while (at >= 0 && view.getUint32(at, true) !== SIGNATURE.end) at -= 1;
  if (at < 0) {
    throw new ZipFormatError("That doesn't look like a VisiList backup file.");
  }

  const count = view.getUint16(at + 10, true);
  const start = view.getUint32(at + 16, true);
  const size = view.getUint32(at + 12, true);

  const directory = new DataView(await file.slice(start, start + size).arrayBuffer());
  const entries = new Map<string, Entry>();
  let cursor = 0;

  for (let n = 0; n < count; n += 1) {
    if (cursor + 46 > directory.byteLength || directory.getUint32(cursor, true) !== SIGNATURE.central) {
      throw new ZipFormatError('That backup file is damaged and can’t be read.');
    }

    const nameLength = directory.getUint16(cursor + 28, true);
    const extraLength = directory.getUint16(cursor + 30, true);
    const commentLength = directory.getUint16(cursor + 32, true);

    const name = new TextDecoder().decode(
      new Uint8Array(directory.buffer, cursor + 46, nameLength),
    );

    entries.set(name, {
      name,
      method: directory.getUint16(cursor + 10, true),
      crc: directory.getUint32(cursor + 16, true),
      compressed: directory.getUint32(cursor + 20, true),
      size: directory.getUint32(cursor + 24, true),
      header: directory.getUint32(cursor + 42, true),
    });

    cursor += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

async function read(file: Blob, entry: Entry): Promise<Blob> {
  // The local header repeats the name and may carry different extra fields, so
  // its own lengths are the only way to find where the bytes start.
  const header = new DataView(await file.slice(entry.header, entry.header + 30).arrayBuffer());
  if (header.byteLength < 30 || header.getUint32(0, true) !== SIGNATURE.local) {
    throw new ZipFormatError(`${entry.name} is missing from that backup.`);
  }

  const start =
    entry.header + 30 + header.getUint16(26, true) + header.getUint16(28, true);
  const packed = file.slice(start, start + entry.compressed);

  const unpacked = entry.method === STORED ? packed : await inflate(packed, entry);

  // Copied out of `file`, not returned as a slice of it, and that copy is the
  // whole point. A slice is a view onto the backup the user picked, still on
  // disk — and WebKit stores such a blob in IndexedDB by copying the entire
  // file it points into, once per blob. Restoring a 166MB backup of 2268
  // photos wrote about 180GB before it filled the disk, at which point
  // `bulkPut` began failing with "Error preparing Blob/File data to be stored
  // in object store" and the restore died a third of the way in. Chromium
  // copies only the sliced range, which is why this never showed on Windows.
  //
  // One entry at a time, so the memory note at the top of this file still
  // holds: a photo costs memory while it is read and until it is stored.
  const bytes = await unpacked.arrayBuffer();
  verify(new Uint8Array(bytes), entry);

  return new Blob([bytes]);
}

async function inflate(packed: Blob, entry: Entry): Promise<Blob> {
  if (entry.method !== DEFLATED) {
    throw new ZipFormatError(`${entry.name} is compressed in a way VisiList can’t read.`);
  }

  const stream = packed.stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return await new Response(stream).blob();
}

/**
 * The reason any of this checksums at all: a backup is often the only copy of
 * a photo, and a file that has rotted should say so here rather than restore
 * an item whose picture is half grey.
 */
function verify(bytes: Uint8Array, entry: Entry): void {
  if (crc32(bytes) !== entry.crc) {
    throw new ZipFormatError(`${entry.name} is damaged in that backup file.`);
  }
}
